/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {ClassNameMap} from '@material-ui/core/styles/withStyles';
import {translate} from '@ion/core/src/locale/locale';
import {PlatformDeviceTypes} from '@ion/core/src/types/entities';
import Accordion from '@ion/dls/src/components/accordion/Accordion';
import CheckBox from '@ion/dls/src/components/checkbox/CheckBox';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {InfoIcon} from '@ion/dls/src/icons/InfoIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {MAX_INITIAL_FACETS_GROUPS} from './JobFacetsDrawer';
import {Aggregation} from './JobFacetUtils';

interface PlatformFacetAccordionProps {
  classes: ClassNameMap;
  activeTab: string;
  isLoading: boolean;
  deviceTypes: PlatformDeviceTypes;
  showMoreFacets: Record<string, boolean>;
  platformIdAggregations: Aggregation[];
  toggleShowMore: (category: string) => void;
  setPlatformId: (platformId: string) => void;
  setIsFileTypeChecked: (isFileTypeChecked: boolean) => void;
  clearFacetCategory: (facetCategory: string, activeTab: string) => void;
  handleFacetChange: (facet: [string, string], isChecked: boolean, activeTab: string) => void;
  hasSelectedJobsFacets: (tab: string, category?: string | undefined, value?: string | undefined) => boolean;
  getSelectedFacetsForTab: (tab: string) => {
    [category: string]: string[];
  };
};

/**
 * Renders a grouped accordion list of platform facet filters with counts.
 *
 * The component dynamically displays device platforms and their corresponding file types
 * using aggregation data. Each file type includes a selectable checkbox to filter results,
 * and users can clear selections or toggle between showing more or fewer categories.
 *
 * <PlatformFacetAccordion
 *   classes={classes}
 *   deviceTypes={[
 *     {
 *       name: 'Mobile',
 *       fileTypes: [{ platformId: 'ios', name: 'iOS' }, { platformId: 'android', name: 'Android' }]
 *     },
 *     {
 *       name: 'Web',
 *       fileTypes: [{ platformId: 'web', name: 'Web App' }]
 *     }
 *   ]}
 *   aggregations={[
 *     {
 *       name: 'tasks.metadata.platformId-terms',
 *       value: {
 *         buckets: [
 *           { key: 'ios', count: 14 },
 *           { key: 'android', count: 8 },
 *           { key: 'web', count: 20 }
 *         ]
 *       }
 *     }
 *   ]}
 *   setPlatformId={(id) => console.log('Set platform:', id)}
 *   toggleShowMore={(name) => console.log('Toggled show more for:', name)}
 *   handleFacetChange={(facetName, id, checked) => console.log(facetName, id, checked)}
 *   clearFacetCategory={(facetName) => console.log('Cleared:', facetName)}
 *   isLoading={false}
 * />
 *
 * Internally, the component:
 *  - Merges device type data with aggregation counts.
 *  - Filters out platforms with count zero.
 *  - Groups platforms under device type accordions.
 *  - Allows toggling expanded views for long lists of facets.
 *  - Respects loading state by disabling checkboxes when `isLoading` is true.
 */
const PlatformFacetAccordion = ({
  classes,
  activeTab,
  deviceTypes,
  showMoreFacets,
  setPlatformId,
  toggleShowMore,
  handleFacetChange,
  clearFacetCategory,
  setIsFileTypeChecked,
  hasSelectedJobsFacets,
  platformIdAggregations,
  getSelectedFacetsForTab,
  isLoading = false
}: PlatformFacetAccordionProps) => {
  const platformIdAggregation = platformIdAggregations?.find(agg => agg?.name === 'tasks.metadata.platformId-terms');
  const buckets               = platformIdAggregation?.value?.buckets;
  const aggregationName       = platformIdAggregation?.name ?? '';
  const showAllFacets         = showMoreFacets?.[aggregationName];
  const title                 = translate(`jobsearchengine:facets.category.${aggregationName}.title`);
  const tooltip               = translate(`jobsearchengine:facets.category.${aggregationName}.tooltip`);

  const categorized = deviceTypes?.map(({name, fileTypes = []}) => {
    const files = fileTypes?.map(fileType => {
      const bucket = buckets?.find(({key = {}}) => key === fileType?.platformId);
      return {...fileType, count: bucket?.count || 0};
    }).filter(({count = {}}) => count > 0);
    return {deviceTypeName: name, files};
  }).filter(({files = {}}) => files?.length);

  const categoriesToShow = showAllFacets
    ? categorized
    : categorized?.slice(0, MAX_INITIAL_FACETS_GROUPS);

  return (
    <div className={classes.accordionWrapper}>
      <Accordion defaultExpanded isCondensedVariant
        className={classes.accordion}
        header={
          <div className={classes.categoryTitle}>
            {<div className={classes.titleTooltipWrapper}>
              {title}
              <Tooltip arrow
                title={tooltip}
                placement={'bottom-start'}
                width='250px'>
                <span>
                  <InfoIcon size={21} stroke={dlsColor('grey3')}/>
                </span>
              </Tooltip>
            </div>}
            {hasSelectedJobsFacets(activeTab, aggregationName) && (
              <div onClick={function(e) {
                e.stopPropagation();
                e.preventDefault();
                setPlatformId('');
                setIsFileTypeChecked(false);
                clearFacetCategory(aggregationName, activeTab);
              }}
              className={classes.clearAction}>
                {translate('jobsearchengine:facets.clear')}
              </div>
            )}
          </div>
        }
        details={
          <div className={classes.details}>
            {categoriesToShow?.map(category => {
              const selectedPlatformIds = getSelectedFacetsForTab(activeTab)?.[aggregationName] || [];
              const isCategorySelected  = category?.files?.some(fileType =>
                selectedPlatformIds?.includes(fileType?.platformId)
              );

              return (
                <Accordion isFeatureAccordion isCondensedVariant margin='0px !important'
                  defaultExpanded={isCategorySelected}
                  key={category?.deviceTypeName}
                  className={classes.accordion}
                  backgroundColor={dlsColor('grey5')}
                  borderBottom={'none'}
                  padding={'0px'}
                  header={
                    <span className={classes.categoryTitle}>
                      {category?.deviceTypeName} ({category?.files?.reduce((item, file) => item + file?.count, 0)})
                    </span>
                  }
                  details={
                    <div className={classes.checkBoxPosition}>
                      {category?.files?.map(fileType => (
                        <CheckBox key={fileType?.platformId}
                          disabled={fileType?.count === 0 || isLoading}
                          label={
                            <span className={isLoading ? classes.checkBoxLabelDisabled : classes.checkBoxLabel}>
                              {fileType?.name} ({fileType?.count})
                            </span>
                          }
                          checked={
                            getSelectedFacetsForTab(activeTab)
                              ?.[aggregationName]
                              ?.includes(fileType?.platformId) ||
                          false
                          }
                          onChange={function(e) {
                            handleFacetChange(
                              [aggregationName, fileType?.platformId],
                              e?.target?.checked,
                              activeTab
                            );
                          }}>
                          <span>({fileType?.count})</span>
                        </CheckBox>
                      ))}
                    </div>
                  } />
              );
            })}
            {categorized?.length > MAX_INITIAL_FACETS_GROUPS && (
              <div className={classes.showAction}
                onClick={function(e) {
                  e.stopPropagation();
                  e.preventDefault();
                  toggleShowMore(aggregationName);
                }}>
                {translate(`jobsearchengine:facets.${showAllFacets ? 'showLess' : 'showMore'}`)}
              </div>
            )}
          </div>
        }
        backgroundColor={dlsColor('grey5')}
        borderBottom={'none'}
        padding={'0px'} />
    </div>
  );
};

export default PlatformFacetAccordion;
